import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

import {
    autoSizeColumnTree,
    Cell,
    CellNode,
    Column,
    Context,
    DivRendererOptions,
    ExperimentalNgGridComponent,
    GridContent,
    Row,
    RowNode,
    SelectTemplateFn,
    Sort,
    SortOrder,
} from '../../coaching-observation/experimental.grid';
import { TranslationKeyMap } from '../coaching-insight.i18n';
import { RosterType } from '../coverage.coaching-insight.model';
import {
    EventParticipant,
    EventType,
} from '../schedule/models/schedule-event.model';

import {
    CellContext,
    ColumnContext,
    ColumnType,
    OtherScheduleItemsClickEvent,
    PeriodClickEvent,
    RowContext,
} from './coaching-coverage-grid.model';

// TODO revise. a necessary evil since styling is only known by the presenting component
function measureColumns(columns: Array<Column<ColumnContext>>): void {
    if (!columns) {
        return;
    }
    const column = columns.find((e) => e.id === ColumnType.NAME.toString());
    column.willMeasure = (node) => {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (node.renderContext && node.renderContext.element) {
            node.renderContext.element.style.width = '';
        }
    };
    column.didMeasure = (node) => {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (node.renderContext && node.renderContext.element) {
            node.renderContext.element.style.width = '100%';
        }
    };
}

@Component({
    selector: '[cmpsCoachingCoverageGrid]',
    templateUrl: './coaching-coverage-grid.component.html',
    host: {
        'data-e2e': 'component-coaching-coverage-grid',
    },
})
export class CoachingCoverageGridComponent implements OnChanges, OnInit {
    @ViewChild('grid') private readonly _grid: ExperimentalNgGridComponent;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _ColumnType = ColumnType;
    /** @private */ _SortOrder = SortOrder;
    /** @private */ _RosterType = RosterType;
    /** @private */ _EventType = EventType;

    @Input() columns: Array<Column<ColumnContext>>;
    @Input() rows: Array<Row<RowContext, CellContext>>;
    @Input() sortDefs: Sort[];
    @Input() columnsNeedResize: string[];
    @Input() needsLayout: number;
    @Input() rosterType: RosterType;

    @Output() sortChange: EventEmitter<Column>;
    @Output() togglePeriodColumnClick: EventEmitter<Column<ColumnContext>>;
    @Output() toggleRowExpandClick: EventEmitter<Row<RowContext, CellContext>>;
    @Output() periodClick: EventEmitter<PeriodClickEvent>;
    @Output() rosterTypeChange: EventEmitter<RosterType>;
    @Output() scheduleForUserClick: EventEmitter<EventParticipant>; /* userId */
    @Output()
    otherScheduleItemsClick: EventEmitter<OtherScheduleItemsClickEvent>;

    /** @private */ _renderOpts: DivRendererOptions;

    /** @private */ _selectTpl: SelectTemplateFn = (
        context: Context<ColumnContext, any, ColumnContext | CellContext>,
        contents: QueryList<GridContent>,
    ) => {
        if (context.t === 'grid') {
            return contents.find((e) => e.type === 'grid');
        }
        if (context.t === 'row') {
            return contents.find((e) => e.type === 'row');
        }
        if (context.t === 'cell') {
            return contents.find(
                (e) => e.type === 'cell' && e.id === context.ctx.templateId,
            );
        }
    };

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _changeDetectorRef: ChangeDetectorRef,
    ) {
        this.sortChange = new EventEmitter();
        this.togglePeriodColumnClick = new EventEmitter();
        this.toggleRowExpandClick = new EventEmitter();
        this.periodClick = new EventEmitter();
        this.rosterTypeChange = new EventEmitter();
        this.scheduleForUserClick = new EventEmitter();
        this.otherScheduleItemsClick = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('columns')) {
            measureColumns(this.columns);
            this._renderOpts = {
                ...this._renderOpts,
                columns: this.columns,
            };
        } else {
            // if columns is present an inflate will occur later
            // eslint-disable-next-line no-prototype-builtins
            if (changes.hasOwnProperty('rows')) {
                this._onInflate(true);
                // TODO revise. only first index is used
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this._grid.renderer.autoSizeColumns([
                    ColumnType.NAME.toString(),
                ]);
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this._grid.renderer.autoSizeColumns([
                    ColumnType.OA_SCORE.toString(),
                ]);
            }
            // eslint-disable-next-line no-prototype-builtins
            if (changes.hasOwnProperty('sortDefs')) {
                this._sort();
            }
            // eslint-disable-next-line no-prototype-builtins
            if (changes.hasOwnProperty('columnsNeedResize')) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                autoSizeColumnTree(
                    this._grid.renderer.contentColumns.columns.children,
                    this._grid.renderer,
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    this.columns.filter(
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        (e) => this.columnsNeedResize.indexOf(e.id) !== -1,
                    ),
                    /* .reduce((p, c) => {
                        p.push(...c.children.filter(e => !e.id.startsWith(ColumnType.COACHING_COVERAGE_PERCENT.toString())));
                        return p;
                    }, []) */
                );
            }
            // eslint-disable-next-line no-prototype-builtins
            if (changes.hasOwnProperty('needsLayout')) {
                this._grid.gridNode.detectChanges();
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this._grid.renderer.layout();
            }
        }
    }

    ngOnInit(): void {
        measureColumns(this.columns);
        this._renderOpts = {
            columns: this.columns,
            defaultRenderFn: null,
            height: 700,
            horizontalScrollPadding: 9999,
        };
    }

    /** @private */ _onSortClick(column: Column<ColumnContext>): void {
        this._ngZone.run(() => {
            this.sortChange.emit(column);
        });
    }

    /** @private */ _onTogglePeriodColumnClick(
        column: Column<ColumnContext>,
    ): void {
        this._ngZone.run(() => {
            this.togglePeriodColumnClick.emit(column);
        });
    }

    /** @private */ _onToggleRowExpandClick(
        $event: Row<RowContext, CellContext>,
    ): void {
        this._ngZone.run(() => {
            this.toggleRowExpandClick.emit($event);
        });
    }

    /** @private */ _onPeriodClick($event: Column<ColumnContext>): void {
        this._ngZone.run(() => {
            this.periodClick.emit({
                column: $event,
                elementSelector: `[data-ccg="period-${$event.id}"]`,
            });
        });
    }

    /** @private */ _onRosterTypeClick($event: RosterType): void {
        this._ngZone.run(() => {
            if ($event !== this.rosterType) {
                this.rosterTypeChange.emit($event);
            }
        });
    }

    /** @private */ _onOtherScheduleItemsClick($event: CellNode): void {
        this._ngZone.run(() => {
            const column = $event.context.c as Column<ColumnContext>;
            const def = $event.def as Cell<CellContext>;
            this.otherScheduleItemsClick.emit({
                cell: $event.def,
                type:
                    column.ctx.type === ColumnType.OBSERVATION_COVERAGE_PERCENT
                        ? EventType.OBSERVATION
                        : EventType.COMMITMENT,
                elementSelector: `[data-ccg="schedule-${def.columnId}-${def.ctx.user.userId}"] button`,
            });
        });
    }

    /** @private */ _onInflate(notFromGrid?: boolean): void {
        this._changeDetectorRef.detectChanges(); // best effort to make sure _grid is set
        if (!this._grid) {
            return;
        }
        this._grid.gridNode.setChildren(this.rows || []);
        this._sort();
        if (!notFromGrid) {
            // TODO revise. only first index is used
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._grid.renderer.autoSizeColumns([ColumnType.NAME.toString()]);
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._grid.renderer.autoSizeColumns([
                ColumnType.OA_SCORE.toString(),
            ]);
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._grid.renderer.autoSizeColumnTree(); // TODO revise only auto sizes content columns
        }
    }

    /** @private */ _onScheduleForUserClick(performerUser: any): void {
        this._ngZone.run(() => {
            const user: EventParticipant = {
                eventId: 0,
                firstName: performerUser.fullName,
                userId: performerUser.userId,
            };

            this.scheduleForUserClick.emit(user);
        });
    }

    private _sort(): void {
        const maxDepth =
            this._grid.gridNode.reduce<number>(
                (p, c: RowNode<RowContext>) => Math.max(c.context.ctx.depth, p),
                0,
            ) - 1;
        if (maxDepth <= 0) {
            this._grid.gridNode.sort(this.sortDefs);
        } else {
            this._grid.gridNode
                .filter((n) => n.context.ctx.depth === maxDepth)
                .forEach((n) =>
                    n.sort(this.sortDefs, null, { emitEvent: true }),
                );
        }
    }
}
